import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const BLANK_EVENT = {
  title: 'Sunday Meet-Up',
  location: '',
  event_date: '',
  event_time: '18:00',
  max_orders: 20,
  active: true,
  notes: '',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function MeetUpPanel() {
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null); // null | 'new' | event object
  const [form, setForm]         = useState(BLANK_EVENT);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { fetchEvents(); }, []);

  async function fetchEvents() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data } = await supabase
        .from('meet_up_events')
        .select('*')
        .order('event_date', { ascending: true });
      setEvents(data || []);
    } else {
      setEvents([]);
    }
    setLoading(false);
  }

  function openNew() {
    setForm(BLANK_EVENT);
    setEditing('new');
    setError('');
  }

  function openEdit(event) {
    setForm({
      title:      event.title || 'Sunday Meet-Up',
      location:   event.location || '',
      event_date: event.event_date || '',
      event_time: event.event_time ? event.event_time.slice(0, 5) : '18:00',
      max_orders: event.max_orders || 20,
      active:     event.active !== false,
      notes:      event.notes || '',
    });
    setEditing(event);
    setError('');
  }

  async function handleSave() {
    if (!form.location.trim()) { setError('Location is required.'); return; }
    if (!form.event_date)      { setError('Event date is required.'); return; }
    setSaving(true);
    setError('');

    const payload = {
      title:      form.title.trim() || 'Sunday Meet-Up',
      location:   form.location.trim(),
      event_date: form.event_date,
      event_time: form.event_time + ':00',
      max_orders: Number(form.max_orders),
      active:     form.active,
      notes:      form.notes.trim() || null,
    };

    try {
      if (editing === 'new') {
        const { error: err } = await supabase.from('meet_up_events').insert(payload);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('meet_up_events').update(payload).eq('id', editing.id);
        if (err) throw err;
      }
      await fetchEvents();
      setEditing(null);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this meet-up event? This cannot be undone.')) return;
    await supabase.from('meet_up_events').delete().eq('id', id);
    setEvents(prev => prev.filter(e => e.id !== id));
  }

  async function toggleActive(event) {
    const newActive = !event.active;
    setEvents(prev => prev.map(e => e.id === event.id ? { ...e, active: newActive } : e));
    await supabase.from('meet_up_events').update({ active: newActive }).eq('id', event.id);
  }

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-charcoal">Meet-Up Events</h2>
          <p className="text-sage-500 text-sm mt-1">Schedule and manage Sunday night pick-up events.</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2">
          <SafeIcon name="Plus" /> New Event
        </button>
      </div>

      {/* Event list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sage-100 p-12 text-center">
          <SafeIcon name="Calendar" className="text-sage-300 text-4xl mx-auto mb-3" />
          <p className="text-sage-400 italic">No meet-up events yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => {
            const isPast = event.event_date < today;
            return (
              <div
                key={event.id}
                className={`bg-white rounded-2xl border p-5 flex items-start justify-between gap-4 ${
                  isPast
                    ? 'border-sage-100 opacity-60'
                    : event.active
                    ? 'border-blush-100 shadow-soft'
                    : 'border-sage-100'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isPast ? 'bg-sage-100' : event.active ? 'bg-blush-50' : 'bg-sage-100'
                  }`}>
                    <SafeIcon name="MapPin" className={isPast ? 'text-sage-400' : event.active ? 'text-blush-400' : 'text-sage-400'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <h4 className="font-serif text-lg text-charcoal">{event.title}</h4>
                      {isPast && (
                        <span className="text-[10px] font-bold text-sage-400 uppercase tracking-wider bg-sage-100 px-2 py-0.5 rounded-full">Past</span>
                      )}
                      {!isPast && event.active && (
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      )}
                      {!isPast && !event.active && (
                        <span className="text-[10px] font-bold text-sage-400 uppercase tracking-wider bg-sage-100 px-2 py-0.5 rounded-full">Hidden</span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-charcoal">{formatDate(event.event_date)} · {formatTime(event.event_time)}</p>
                    <p className="text-sm text-sage-500 flex items-center gap-1 mt-0.5">
                      <SafeIcon name="MapPin" className="text-xs shrink-0" />
                      {event.location}
                    </p>
                    <p className="text-xs text-sage-400 mt-1">
                      Max {event.max_orders} orders{event.notes ? ` · ${event.notes}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isPast && (
                    <button
                      onClick={() => toggleActive(event)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                        event.active
                          ? 'border-sage-200 text-sage-500 hover:bg-sage-50'
                          : 'border-green-200 text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {event.active ? 'Hide' : 'Activate'}
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(event)}
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-sage-200 text-sage-400 hover:bg-sage-50 transition-colors"
                    title="Edit"
                  >
                    <SafeIcon name="Edit2" className="text-sm" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                    title="Delete"
                  >
                    <SafeIcon name="Trash2" className="text-sm" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center px-4"
            onClick={() => setEditing(null)}
          >
            <div className="absolute inset-0 bg-charcoal/40 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              className="relative bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-lg p-8 space-y-6 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-charcoal">
                  {editing === 'new' ? 'New Meet-Up Event' : 'Edit Event'}
                </h3>
                <button
                  onClick={() => setEditing(null)}
                  className="w-9 h-9 rounded-full bg-sage-100 flex items-center justify-center text-sage-400 hover:bg-sage-200 transition-colors"
                >
                  <SafeIcon name="X" />
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Event Title</label>
                  <input
                    type="text"
                    className="input-field w-full"
                    value={form.title}
                    onChange={e => setF('title', e.target.value)}
                    placeholder="e.g. Sunday Meet-Up"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Location *</label>
                  <input
                    type="text"
                    className="input-field w-full"
                    value={form.location}
                    onChange={e => setF('location', e.target.value)}
                    placeholder="e.g. Desert Ridge Marketplace, Phoenix AZ"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Event Date *</label>
                    <input
                      type="date"
                      className="input-field w-full"
                      value={form.event_date}
                      onChange={e => setF('event_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Start Time</label>
                    <input
                      type="time"
                      className="input-field w-full"
                      value={form.event_time}
                      onChange={e => setF('event_time', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Max Orders</label>
                  <input
                    type="number"
                    className="input-field w-full"
                    value={form.max_orders}
                    min={1}
                    onChange={e => setF('max_orders', parseInt(e.target.value) || 1)}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-sage-500 uppercase tracking-wider block mb-2">Notes (optional)</label>
                  <textarea
                    className="input-field w-full min-h-[80px] resize-none"
                    value={form.notes}
                    onChange={e => setF('notes', e.target.value)}
                    placeholder="Special instructions or info for customers..."
                  />
                </div>

                {/* Active toggle */}
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <div className="relative" onClick={() => setF('active', !form.active)}>
                    <div className={`w-12 h-6 rounded-full transition-colors ${form.active ? 'bg-blush-300' : 'bg-sage-200'}`} />
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.active ? 'translate-x-[26px]' : 'translate-x-0.5'}`} />
                  </div>
                  <div>
                    <p className="font-bold text-charcoal text-sm">Active</p>
                    <p className="text-xs text-sage-400">Customers can see and order for this event</p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditing(null)} className="btn-secondary flex-1">Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                  ) : (
                    <><SafeIcon name="Save" /> Save Event</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
